/**
 * SitesPage
 *
 * Standalone Sites page (May 2026 Figma redesign).
 *
 *   - 4 KPI summary cards at the top: Total sites / Total body cameras /
 *     Total checkouts / Total body camera users.
 *   - Section toolbar: search icon-button, "Sites" + "N TOTAL" subtitle,
 *     overflow (...) button, "+ Add site" pill, view-toggle.
 *   - Table columns: select / SITE NAME / PROFILE / BODY CAMERAS /
 *     CHECKOUTS / USERS / NETWORKS / edit-and-delete actions.
 *
 * Per-site counts (body cameras, checkouts, users) are derived client-side
 * by fetching the org's devices / checkouts / users once and grouping by
 * siteId. NETWORKS is a placeholder (backend has no networks concept yet).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import organizationService from '../services/organizationService';
import checkoutService from '../services/checkoutService';
import deviceService from '../services/deviceService';
import userService from '../services/userService';
import { useTenant } from '../context/TenantContext';
import { toast } from '../components/Toast';
import { Country, State } from 'country-state-city';
import SiteSettingsModal from '../components/sites/SiteSettingsModal';

const ICON_BASE =
  `${(import.meta.env.BASE_URL || '/').replace(/\/$/, '')}/icons`;
const CHK_ICON = `${ICON_BASE}/checkouts-page`;
const NAV_ICON = `${ICON_BASE}/common-left-nav`;

const ALLOWED_COUNTRY_CODES = ['US', 'IN', 'AU', 'CN', 'CA', 'AE', 'ZA'];
const ALL_COUNTRIES = Country.getAllCountries()
  .filter((c) => ALLOWED_COUNTRY_CODES.includes(c.isoCode))
  .map((c) => ({ code: c.isoCode, name: c.name }));

const COLUMNS = [
  { id: 'siteName',    label: 'SITE NAME' },
  { id: 'profile',     label: 'PROFILE' },
  { id: 'bodyCameras', label: 'BODY CAMERAS' },
  { id: 'checkouts',   label: 'CHECKOUTS' },
  { id: 'users',       label: 'USERS' },
  { id: 'networks',    label: 'NETWORKS' },
];


/* -------------------------------------------------------------------------- */
/*  KPI card                                                                  */
/* -------------------------------------------------------------------------- */
function KpiCard({ icon, value, label }) {
  const [broken, setBroken] = useState(false);
  return (
    <div className="sp-kpi">
      <div className="sp-kpi-icon">
        {!broken ? (
          <img src={icon} alt="" onError={() => setBroken(true)} />
        ) : (
          <i className="fas fa-circle-info"></i>
        )}
      </div>
      <div className="sp-kpi-text">
        <div className="sp-kpi-value">{value}</div>
        <div className="sp-kpi-label">{label}</div>
      </div>
    </div>
  );
}

function countryCodeFromName(name) {
  if (!name) return '';
  if (name.length === 2) return name.toUpperCase();
  const hit = ALL_COUNTRIES.find((c) => c.name.toLowerCase() === String(name).toLowerCase());
  return hit ? hit.code : name;
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                      */
/* -------------------------------------------------------------------------- */

function SitesPage() {
  const { orgId, currentOrg, refreshSites } = useTenant();
  const [sites, setSites]           = useState([]);
  const [devices, setDevices]       = useState([]);
  const [checkouts, setCheckouts]   = useState([]);
  const [users, setUsers]           = useState([]);
  const [loading, setLoading]       = useState(false);
  const [modalOpen, setModalOpen]   = useState(false);
  const [editing, setEditing]       = useState(null);
  const [saving, setSaving]         = useState(false);
  const [query, setQuery]           = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [sort, setSort]             = useState({ key: 'siteName', asc: true });
  const [selected, setSelected]     = useState(() => new Set());

  /** Fetch sites + best-effort aggregations (devices / checkouts / users). */
  const load = useCallback(async () => {
    if (!orgId) {
      setSites([]); setDevices([]); setCheckouts([]); setUsers([]);
      return;
    }
    setLoading(true);
    try {
      const [siteRes, deviceRes, checkoutRes, userRes] = await Promise.allSettled([
        organizationService.listSites(orgId, 0, 500),
        deviceService.listDevices(orgId, 1, { limit: 500, offset: 0 }),
        checkoutService.listCheckouts(orgId, { limit: 500, offset: 0 }),
        userService.listUsers(orgId, 0, 500),
      ]);
      const unwrap = (r) => {
        if (r.status !== 'fulfilled') return [];
        const d = r.value;
        const list = Array.isArray(d) ? d : (d?.content ?? d?.items ?? d?.data ?? []);
        return Array.isArray(list) ? list : [];
      };
      setSites(unwrap(siteRes));
      setDevices(unwrap(deviceRes));
      setCheckouts(unwrap(checkoutRes));
      setUsers(unwrap(userRes));
      if (siteRes.status === 'rejected') {
        toast.error(siteRes.reason?.response?.data?.message || siteRes.reason?.message || 'Failed to load sites');
      }
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  /** Group devices / checkouts / users by siteId. */
  const aggregates = useMemo(() => {
    const bump = (m, k) => m.set(k, (m.get(k) || 0) + 1);
    const dev = new Map();
    const chk = new Map();
    const usr = new Map();
    devices.forEach((d)   => { const s = d.siteId ?? d.site_id; if (s != null) bump(dev, s); });
    checkouts.forEach((c) => { const s = c.siteId ?? c.site_id; if (s != null) bump(chk, s); });
    users.forEach((u) => {
      if (Array.isArray(u.siteIds) && u.siteIds.length > 0) {
        u.siteIds.forEach((s) => bump(usr, s));
      } else {
        const s = u.siteId ?? u.site_id;
        if (s != null) bump(usr, s);
      }
    });
    return { dev, chk, usr };
  }, [devices, checkouts, users]);

  /** Merge sites with per-site counts. */
  const rows = useMemo(() => sites.map((s) => {
    const id = s.siteId ?? s.id;
    return {
      raw:         s,
      id,
      siteName:    s.siteName || '—',
      profile:     s.profile || s.profileName || s.template || 'Standard',
      bodyCameras: aggregates.dev.get(id) ?? 0,
      checkouts:   aggregates.chk.get(id) ?? 0,
      users:       aggregates.usr.get(id) ?? 0,
      networks:    s.networkCount ?? 0,
    };
  }), [sites, aggregates]);

  /** Search + sort. */
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const data = !q ? rows : rows.filter((r) =>
      [r.siteName, r.profile].some((v) => String(v ?? '').toLowerCase().includes(q)),
    );
    return [...data].sort((a, b) => {
      const av = a[sort.key];
      const bv = b[sort.key];
      if (typeof av === 'number' && typeof bv === 'number') return (sort.asc ? 1 : -1) * (av - bv);
      return (sort.asc ? 1 : -1) * String(av ?? '').localeCompare(String(bv ?? ''));
    });
  }, [rows, query, sort]);

  const toggleSort = (key) =>
    setSort((p) => (p.key === key ? { key, asc: !p.asc } : { key, asc: true }));

  /** KPI totals. */
  const totals = useMemo(() => ({
    sites:     sites.length,
    devices:   devices.length,
    checkouts: checkouts.length,
    users:     users.length,
  }), [sites, devices, checkouts, users]);

  /* ---- Selection ---- */
  const allChecked  = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allChecked) filtered.forEach((r) => next.delete(r.id));
      else filtered.forEach((r) => next.add(r.id));
      return next;
    });
  };
  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  /* ---- CRUD ---- */
  const handleAdd = () => {
    if (!orgId) {
      toast.warning('Select an organization on the System page first.');
      return;
    }
    setEditing(null);
    setModalOpen(true);
  };

  const handleEdit = (row) => {
    const s = row.raw;
    setEditing({
      siteId: s.siteId ?? s.id,
      siteName: s.siteName || '',
      country: countryCodeFromName(s.country),
      state: s.state || '',
      city: s.city || '',
      addressLine1: s.addressLine1 || '',
      timezone: s.timezone || '',
      regionName: s.regionName || '',
      zipCode: s.zipCode || '',
      description: s.description || '',
    });
    setModalOpen(true);
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete site "${row.siteName}"?`)) return;
    try {
      await organizationService.deleteSite(row.id, orgId);
      toast.success('Site deleted');
      await load();
      refreshSites();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to delete site');
    }
  };

  const handleSave = async (payload) => {
    setSaving(true);
    try {
      if (editing) {
        await organizationService.updateSite(editing.siteId, payload, orgId);
        toast.success('Site updated');
      } else {
        await organizationService.createSite({ ...payload, orgId });
        toast.success('Site created');
      }
      setModalOpen(false);
      setEditing(null);
      await load();
      refreshSites();
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {/* ---- KPI cards ---- */}
      <div className="sp-kpis">
        <KpiCard icon={`${NAV_ICON}/site.svg`}     value={totals.sites}     label="Total sites" />
        <KpiCard icon={`${NAV_ICON}/devices.svg`}  value={totals.devices}   label="Total body cameras" />
        <KpiCard icon={`${NAV_ICON}/checkout.svg`} value={totals.checkouts} label="Total checkouts" />
        <KpiCard icon={`${NAV_ICON}/user.svg`}     value={totals.users}     label="Total body camera users" />
      </div>

      {/* ---- Section toolbar ---- */}
      <div className="sp-toolbar">
        <div className="sp-toolbar-left">
          <button
            type="button"
            className="cm-icon-btn"
            title="Search"
            aria-label="Search sites"
            onClick={() => setSearchOpen((v) => !v)}
          >
            <img src={`${CHK_ICON}/search.svg`} alt="" className="cm-icon-img" />
          </button>
          <div className="sp-heading">
            <h1>Sites</h1>
            <div className="sp-subhead"><strong>{filtered.length}</strong> TOTAL</div>
            {searchOpen && (
              <div className="cm-search-inline">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  autoFocus
                  placeholder="Search sites..."
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            )}
          </div>
        </div>
        <div className="sp-toolbar-right">
          <button type="button" className="cm-icon-btn cm-icon-btn-circle" title="More" aria-label="More">
            <i className="fas fa-ellipsis-h"></i>
          </button>
          <button type="button" className="btn-primary-pill" onClick={handleAdd}>
            <i className="fas fa-plus"></i> Add site
          </button>
          <button type="button" className="cm-icon-btn sp-view-toggle" title="Toggle view" aria-label="Toggle view">
            <i className="fas fa-table-list"></i>
          </button>
        </div>
      </div>

      {/* ---- Empty / loading / table ---- */}
      {!orgId ? (
        <div className="empty-state">
          <i className="fas fa-building"></i>
          <div>Select an organization on the <strong>System</strong> page to view its sites.</div>
        </div>
      ) : loading && rows.length === 0 ? (
        <div className="empty-state"><i className="fas fa-spinner fa-spin"></i><div>Loading sites…</div></div>
      ) : filtered.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-map-marker-alt"></i>
          <div>No sites yet{currentOrg ? ` for ${currentOrg.displayName || currentOrg.name}` : ''}. Click <strong>+ Add site</strong> to create one.</div>
        </div>
      ) : (
        <table className="data-table cm-table sp-table">
          <thead>
            <tr>
              <th className="cm-col-check">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => { if (el) el.indeterminate = !allChecked && someChecked; }}
                  onChange={toggleAll}
                  aria-label="Select all"
                />
              </th>
              {COLUMNS.map((c) => (
                <th key={c.id}>
                  <button className="data-table-sort" onClick={() => toggleSort(c.id)}>
                    {c.label}
                    <i className={`fas fa-sort${sort.key === c.id ? (sort.asc ? '-up' : '-down') : ''}`}></i>
                  </button>
                </th>
              ))}
              <th className="cm-col-actions" aria-label="Actions"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => (
              <tr key={r.id}>
                <td className="cm-col-check">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggleRow(r.id)}
                    aria-label={`Select ${r.siteName}`}
                  />
                </td>
                <td style={{ fontWeight: 500 }}>{r.siteName}</td>
                <td>{r.profile}</td>
                <td>{r.bodyCameras}</td>
                <td>{r.checkouts}</td>
                <td>{r.users}</td>
                <td>{r.networks}</td>
                <td className="cm-col-actions">
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Edit site"
                    aria-label="Edit site"
                    onClick={() => handleEdit(r)}
                  >
                    <i className="fas fa-pen"></i>
                  </button>
                  <button
                    type="button"
                    className="sp-row-btn"
                    title="Delete site"
                    aria-label="Delete site"
                    onClick={() => handleDelete(r)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <SiteSettingsModal
        open={modalOpen}
        initial={editing}
        saving={saving}
        onClose={() => { setModalOpen(false); setEditing(null); }}
        onSave={handleSave}
      />
    </>
  );
}

export default SitesPage;

